import { ref } from "vue";
import { callStatuses } from "@/src/constants/callConstants";
import { getCampaignCallSid, shouldOpenPostCallForm } from "@/src/utils/callsUtils.js";
import store from "@/src/store";
import router from "@/src/router/index.js";
import eventBus from "@/src/utils/eventBus.js";
import iframeManager from "@/src/services/telephony/IframeManager";
import providerManager from "@/src/services/telephony/providerManager";
import campaignCallHandler from "@/src/services/telephony/campaignCallHandler";
import plivoEventHandler from "./plivoEventHandler";

class PlivoIframeDevice {
  constructor(provider = "plivoInternational") {
    this.provider = provider;
    this.credentials = null;
    this.isReady = ref(false);
    this.callStatus = ref(callStatuses.IDLE);
    this.currentConnection = ref(null);
    this.incomingConnection = ref(null);
    this.actualCallUUID = null;
    this.campaignCallSid = null;
    this.callEnded = false;
    
    this.setupEventListeners();
  }
  
  updateCallUUID(callInfo) {
    try {
      if (!callInfo) {
        return;
      }

      const backendCallUUID = callInfo?.backendCallUUID;
      const callUUID = backendCallUUID || callInfo.callUUID;

      if (!callUUID) {
        return;
      }
      
      const currentCallSid = this.currentConnection.value?.parameters?.CallSid;
      
      if (!currentCallSid || !currentCallSid.startsWith("iframe_call")) {
        return;
      }
      
      this.actualCallUUID = callUUID;
      if (this.currentConnection.value) {
        this.currentConnection.value.parameters.CallSid = callUUID;
      }
    } catch (error) {
      console.error(`${this.provider}: Error updating call UUID:`, error);
    }
  }

  setupEventListeners() {
    iframeManager.on('PLIVO_IFRAME_INCOMING_CALL', (payload) => {
      this.handleIncomingCall(payload);
    });
    
    iframeManager.on('PLIVO_IFRAME_CALL_ANSWERED_EVENT', (payload) => {
      this.callStatus.value = callStatuses.OPEN;
      store.dispatch("calls/updateCallStatus", callStatuses.OPEN);
      
      this.updateCallUUID(payload.callInfo);
      
      if (payload.callInfo?.callUUID) {
        this.campaignCallSid = payload.callInfo?.backendCallUUID || payload.callInfo?.callUUID;
      }

    });
    
    iframeManager.on('PLIVO_IFRAME_CALL_TERMINATED', (payload) => {
      if (!this.callEnded) {
        this.handleCallEnd();
        this.callStatus.value = callStatuses.CLOSED;
      }
    });
    
    iframeManager.on('PLIVO_IFRAME_CALLING', (payload) => {
      this.callStatus.value = callStatuses.CONNECTING;
      this.updateCallUUID(payload.callInfo);
    });

    iframeManager.on('PLIVO_IFRAME_CALL_RINGING', (payload) => {
      this.callStatus.value = callStatuses.RINGING;
      store.dispatch("calls/updateCallStatus", callStatuses.RINGING);
      this.updateCallUUID(payload.callInfo);
    });
    
    iframeManager.on('PLIVO_IFRAME_CALL_FAILED', (payload) => {
      console.error(`[${this.provider}] Call failed:`, payload);
      if (!this.callEnded) {
        this.handleCallEnd();
        this.callStatus.value = callStatuses.CLOSED;
      }
    });
    
    iframeManager.on('PLIVO_IFRAME_MEDIA_CONNECTED', (payload) => {
      this.updateCallUUID(payload.callInfo);
    });
  }
  
  async initialize(credentials) {
    try {
      console.log(`[${this.provider}] Initializing iframe-based device...`);
      
      if (!credentials || typeof credentials !== "object") {
        throw new Error("Valid credentials object is required");
      }
      
      this.credentials = credentials;
      
      if (!iframeManager.iframe) {
        iframeManager.createIframe();
        await iframeManager.waitForReady();
      }
      
      await iframeManager.sendMessageWithResponse('INIT_SDK', {});
      
      await iframeManager.sendMessageWithResponse('LOGIN', {
        username: credentials.username,
        password: credentials.password
      });
      
      this.isReady.value = true;
      
      if (credentials.username) {
        providerManager.setEndpointMapping(credentials.username, this.provider);
      }
      
      console.log(`[${this.provider}] Iframe device initialized successfully`);
      
      await store.dispatch("telephony/setProviderInitialized", {
        provider: this.provider,
        isInitialized: true,
      });
      await store.dispatch("telephony/setProviderDeviceReady", {
        provider: this.provider,
        deviceReady: true,
      });

      // Subscribe to the MQTT campaign topics (campaign_pickup) so the on-answer
      // lead screen-pop works on the iframe path too. The SDK PlivoDevice does this
      // in its initialize(); without it, International-only workspaces never receive
      // the campaign_pickup message and the lead page never auto-opens on answer.
      try {
        await plivoEventHandler.initialize(this);
      } catch (error) {
        console.error(
          `[${this.provider}] Failed to initialize Plivo event handler:`,
          error,
        );
      }

    } catch (error) {
      console.error(`[${this.provider}] Initialization failed:`, error);
      throw error;
    }
  }
  
  async handleIncomingCall(payload) {
    try {
      const { callInfo, callerName, extraHeaders } = payload;
      
      const backendCallUUID = callInfo?.backendCallUUID;
      const callUUID = backendCallUUID || callInfo?.callUUID;
      
      const srcNumber = callInfo?.src || callerName;
      const destEndpoint = callInfo?.dest;
      
      if (!callUUID) {
        console.error(`[${this.provider}] No callUUID received from iframe, cannot proceed`);
        return;
      }
      this.actualCallUUID = backendCallUUID || callUUID;
      
      const campaignId = campaignCallHandler.extractCampaignId(extraHeaders);
      const isCampaignCall = campaignCallHandler.isCampaignCall(campaignId);

      if (isCampaignCall) {
        await this.handleCampaignIncomingCall(callUUID, srcNumber, destEndpoint, campaignId);
        return;
      }

      const parsedHeaders = campaignCallHandler.parseExtraHeaders(extraHeaders);
      const entityId = parsedHeaders.entityId;
      const entityType = parsedHeaders.entityType;
      await this.handleNormalIncomingCall(callUUID, srcNumber, destEndpoint, extraHeaders, entityId, entityType);
    } catch (error) {
      console.error(`[${this.provider}] Error handling incoming call:`, error);
      this.resetCallState();
    }
  }

  async handleCampaignIncomingCall(callUUID, srcNumber, destEndpoint, campaignId) {
    try {
      if (!campaignCallHandler.validateCampaignCallParams(callUUID, srcNumber, destEndpoint, campaignId)) {
        console.error(`[${this.provider}] Invalid campaign call parameters`);
        this.resetCallState();
        return;
      }

      this.callStatus.value = callStatuses.CONNECTING;
      this.callEnded = false;
      this.actualCallUUID = callUUID;
      this.campaignCallSid = callUUID;

      this.currentConnection.value = {
        parameters: {
          CallSid: callUUID,
          From: srcNumber || "",
          To: destEndpoint || "",
        },
        status: () => this.callStatus.value,
        on: () => {},
        sendDigits: (digits) => this.sendDigits(digits),
      };

      const callDetails = campaignCallHandler.createCampaignCallDetails(
        srcNumber,
        destEndpoint,
        campaignId
      );

      callDetails.provider = this.provider;

      const storeUpdateSuccess = await campaignCallHandler.updateStoreForCampaignCall(
        this.provider,
        campaignId,
        callDetails
      );

      if (!storeUpdateSuccess) {
        console.error(`[${this.provider}] Failed to update store for campaign call`);
        this.resetCallState();
        return;
      }

      await this.autoAnswerCampaignCall(callUUID);
    } catch (error) {
      console.error(`[${this.provider}] Error handling campaign incoming call:`, error);
      this.resetCallState();
    }
  }

  async handleNormalIncomingCall(callUUID, srcNumber, destEndpoint, extraHeaders, entityId, entityType) {
    try {
      this.actualCallUUID = callUUID;
      this.callStatus.value = callStatuses.PENDING;
      
      const phoneNumber = srcNumber?.replace(/[@phone.plivo.com]/g, "") || "";
      const parsedHeaders = campaignCallHandler.parseExtraHeaders(extraHeaders);
      const toNumber = parsedHeaders.to || destEndpoint?.replace(/[@phone.plivo.com]/g, "") || "";
      
      this.incomingConnection.value = {
        parameters: {
          CallSid: callUUID,
          From: phoneNumber,
          To: toNumber,
        },
        status: () => this.callStatus.value,
        on: () => {},
        sendDigits: (digits) => this.sendDigits(digits),
      };
      
      store.commit("telephony/setProvider", this.provider);
      
      const callerDetails = {
        name: phoneNumber || "Unknown Caller",
        number: phoneNumber,
        From: phoneNumber,
        To: toNumber,
        status: callStatuses.PENDING,
        provider: this.provider,
        endpoint: destEndpoint,
        entityId: entityId,
        entityType: entityType,
      };
      
      await store.dispatch("calls/startIncomingCall", callerDetails);
    } catch (error) {
      console.error(`[${this.provider}] Error handling normal incoming call:`, error);
      this.resetCallState();
    }
  }

  async autoAnswerCampaignCall(callUUID) {
    try {
      if (!callUUID) {
        throw new Error("Call UUID is required for auto-answer");
      }
      const permissionResult = await campaignCallHandler.requestMicrophonePermission();
      
      if (!permissionResult.success) {
        await this.handleAutoAnswerError(callUUID, permissionResult.error);
        return;
      }

      await iframeManager.sendMessageWithResponse('ANSWER_CALL', {
        callUUID: callUUID
      });

      this.callStatus.value = callStatuses.OPEN;
      this.callEnded = false;
      
      await campaignCallHandler.updateStoreOnCampaignAnswer();
      
      console.log(`[${this.provider}] Campaign call auto-answered successfully`);
    } catch (error) {
      await this.handleAutoAnswerError(callUUID, error);
    }
  }

  async handleAutoAnswerError(callUUID, error) {
    try {
      await campaignCallHandler.handleAutoAnswerError(this.provider, error);

      try {
        await iframeManager.sendMessageWithResponse('REJECT_CALL', {});
      } catch (rejectError) {
        console.error(`[${this.provider}] Error rejecting call:`, rejectError);
      }

      this.resetCallState();
    } catch (err) {
      console.error(`[${this.provider}] Error handling auto-answer error:`, err);
      this.resetCallState();
    }
  }
  
  async makeCall(params) {
    try {
      if (!this.isReady.value) {
        throw new Error(`${this.provider} SDK not ready`);
      }
      
      const extraHeaders = {
        "X-PH-CampaignId": params.CampaignId?.toString() || "0",
        "X-PH-EntityId": params.Id?.toString() || "",
        "X-PH-EntityType": params.entityType || "contact",
        "X-PH-TaskId": params.taskId?.toString() || "0",
        "X-PH-From": params.From,
        "X-PH-UserId": localStorage.getItem("userId")?.toString() || "",
        "X-PH-callerId": params.callerId || "",
      };
      
      this.callStatus.value = callStatuses.CONNECTING;
      this.callEnded = false;
      
      this.currentConnection.value = {
        parameters: {
          CallSid: `iframe_call_${Date.now()}`,
          From: params.From,
          To: params.To,
        },
        status: () => this.callStatus.value,
        on: () => {},
        sendDigits: (digits) => this.sendDigits(digits),
      };
      
      await iframeManager.sendMessageWithResponse('MAKE_CALL', {
        to: params.To,
        from: params.From,
        extraHeaders
      });
      
      return this.currentConnection.value;
    } catch (error) {
      console.error(`[${this.provider}] makeCall failed:`, error);
      this.resetCallState();
      throw error;
    }
  }
  
  async answerIncomingCall() {
    try {
      if (!this.incomingConnection.value) {
        throw new Error("No incoming call to answer");
      }
      const callUUID = this.actualCallUUID;

      if (!callUUID) {
        throw new Error("No callUUID available to answer");
      }
      
      await iframeManager.sendMessageWithResponse('ANSWER_CALL', {
        callUUID
      }); 
      this.callStatus.value = callStatuses.OPEN;
    
      if (this.incomingConnection.value) {
        this.currentConnection.value = {
          ...this.incomingConnection.value,
          status: () => this.callStatus.value,
          sendDigits: (digits) => this.sendDigits(digits),
        };
        this.incomingConnection.value = null;
      }
      console.log(`[${this.provider}] Call answered successfully`);
      return this.currentConnection.value;
    } catch (error) {
      console.error(`[${this.provider}] Error answering call:`, error);
      throw error;
    }
  }
  
  async hangupCall() {
    try {
      
      if (!this.callEnded) {
        this.handleCallEnd();
      }
      await iframeManager.sendMessageWithResponse('HANGUP_CALL', {});
    } catch (error) {
      console.error(`[${this.provider}] Error hanging up:`, error);
      this.resetCallState();
    }
  }
  
  async rejectIncomingCall() {
    try {
      await iframeManager.sendMessageWithResponse('REJECT_CALL', {});
      this.resetCallState();
    } catch (error) {
      console.error(`[${this.provider}] Error rejecting call:`, error);
      this.resetCallState();
    }
  }
  
  async muteCall() {
    try {
      await iframeManager.sendMessageWithResponse('MUTE_CALL', {});
    } catch (error) {
      console.error(`[${this.provider}] Error muting call:`, error);
      throw error;
    }
  }
  
  async unmuteCall() {
    try {
      await iframeManager.sendMessageWithResponse('UNMUTE_CALL', {});
    } catch (error) {
      console.error(`[${this.provider}] Error unmuting call:`, error);
      throw error;
    }
  }
  
  async sendDigits(digits) {
    try {
      await iframeManager.sendMessageWithResponse('SEND_DIGITS', { digits });
    } catch (error) {
      console.error(`[${this.provider}] Error sending digits:`, error);
      throw error;
    }
  }
  
  getDeviceStatus() {
    return {
      isReady: this.isReady.value,
      hasActiveCall: this.callStatus.value !== callStatuses.IDLE && this.callStatus.value !== callStatuses.CLOSED,
      hasIncomingCall: this.callStatus.value === callStatuses.PENDING && !!this.incomingConnection.value,
    };
  }
  
  getIncomingConnection() {
    return this.incomingConnection.value;
  }
  
  getCurrentConnection() {
    return this.currentConnection.value;
  }

  handleCallEnd() {
    try {
      if (this.callEnded) {
        return;
      }
      const isCampaign = store.getters["calls/isCampaign"];   
      const callSid = this.currentConnection.value?.parameters?.CallSid;

      this.callEnded = true;
      if (callSid) {
        if (isCampaign) {
          const shouldOpenForm = shouldOpenPostCallForm();
          const campaignCallSid = getCampaignCallSid(
            this.campaignCallSid,
            plivoEventHandler.getCampaignCallSid(),
          );

          if (campaignCallSid) {
            setTimeout(() => {
              eventBus.emit("call-log-added", true);
            }, 1200);

            const campaignId = store.getters["callsCampaign/currentCampaignId"];

            router.push({
              name: "sales-dialer-campaign-detail",
              params: { id: campaignId },
            });
            
            if (shouldOpenForm) {
              store.dispatch("callsCampaign/setDrawerVisible", true).then();
              eventBus.emit("call-ended", campaignCallSid);
            }
          }

          eventBus.emit("campaign-next-step", true);

          setTimeout(() => {
            store.dispatch("callsCampaign/refreshCampaignDetails").then();
          }, 2000);
        } else {
            eventBus.emit("call-ended", callSid);
        }
      }

      if (isCampaign) {
        this.campaignCallSid = null;
        plivoEventHandler.clearCampaignCallSid();
        store.dispatch("calls/setIsCampaign", false);
        store.dispatch("callsCampaign/updateCallOngoing", false);
      }
      store.dispatch("calls/updateCallStatus", callStatuses.CLOSED);

      store.dispatch("calls/setIsCallConnected", false);

      if (this.currentConnection.value) {
        this.currentConnection.value = null;
        store.dispatch("calls/endOutgoingCall");
      }

      if (this.incomingConnection.value) {
        this.incomingConnection.value = null;
        store.dispatch("calls/endIncomingCall");
      }

      setTimeout(() => {
        eventBus.emit("call-hangup", true);
        this.resetCallState();
      }, 600);
    } catch (error) {
      console.error(`[${this.provider}] Error in handleCallEnd:`, error);
      this.resetCallState();
    }
  }
  
  resetCallState() {
    try {
      this.callStatus.value = callStatuses.IDLE;
      this.currentConnection.value = null;
      this.incomingConnection.value = null;
      this.actualCallUUID = null;
      this.callEnded = false;
    } catch (error) {
      console.error(`[${this.provider}] Error resetting call state:`, error);
    }
  }
  
  async switchToThisProvider() {
    console.log(`[${this.provider}] Already active in iframe`);
  }
  
  ensureEventListenersActive() {
  }
  
  _validateFeature(feature) {
    return true;
  }

  async destroy() {
    try {
      iframeManager.removeAllListeners('PLIVO_IFRAME_INCOMING_CALL');
      iframeManager.removeAllListeners('PLIVO_IFRAME_CALL_ANSWERED_EVENT');
      iframeManager.removeAllListeners('PLIVO_IFRAME_CALL_TERMINATED');
      iframeManager.removeAllListeners('PLIVO_IFRAME_CALLING');
      iframeManager.removeAllListeners('PLIVO_IFRAME_CALL_RINGING');
      iframeManager.removeAllListeners('PLIVO_IFRAME_CALL_FAILED');
      iframeManager.removeAllListeners('PLIVO_IFRAME_MEDIA_CONNECTED');
      this.resetCallState();
      this.isReady.value = false;
    } catch (error) {
      console.error(`[${this.provider}] Error destroying device:`, error);
    }
  }
}
export default PlivoIframeDevice;

